'use client';

import { c as _c } from "react/compiler-runtime";
import type { Dispatch, SetStateAction } from 'react';
import { useEffect } from 'react';
import type { PrV2CommentComparison } from '../lib/pr-v2-comment-version-navigation';
import type { PrV2VersionOption } from './use-pr-v2-version-comparison';
import { resolvePrV2CommentComparison } from '../lib/pr-v2-comment-version-navigation';
export type PrV2PendingScrollTarget = {
  type: 'file';
  filename: string;
} | {
  type: 'group';
  groupId: string;
  alignHeaderToTop?: boolean;
} | {
  type: 'comment';
  commentId: string;
  filename?: string;
  targetHeadSha?: string;
  requiredComparisonKey: string | null;
  comparisonResolved: boolean;
} | {
  type: 'draft';
  filename: string;
};
export type SetPrV2PendingScrollTarget = Dispatch<SetStateAction<PrV2PendingScrollTarget | null>>;
type UsePrV2PendingCommentComparisonParams = {
  canonicalBaseSha: string | null;
  canonicalHeadSha: string | null;
  currentBaseSha: string | null;
  currentHeadSha: string | null;
  comparisonKey: string | null;
  versionOptions: PrV2VersionOption[];
  versionsLoaded: boolean;
  versionsLoading: boolean;
  versionsFetching: boolean;
  versionsError: boolean;
  pendingScrollTarget: PrV2PendingScrollTarget | null;
  setPendingScrollTarget: SetPrV2PendingScrollTarget;
  setComparison: (comparison: PrV2CommentComparison) => void;
};
function buildComparisonKey(baseSha: string | null, headSha: string | null): string | null {
  return baseSha && headSha ? `${baseSha}:${headSha}` : null;
}
export function usePrV2PendingCommentComparison(t0) {
  const $ = _c(15);
  const {
    canonicalBaseSha,
    canonicalHeadSha,
    currentBaseSha,
    currentHeadSha,
    comparisonKey,
    versionOptions,
    versionsLoaded,
    versionsLoading,
    versionsFetching,
    versionsError,
    pendingScrollTarget,
    setPendingScrollTarget,
    setComparison
  } = t0;
  let t1;
  let t2;
  if ($[0] !== canonicalBaseSha || $[1] !== canonicalHeadSha || $[2] !== comparisonKey || $[3] !== currentBaseSha || $[4] !== currentHeadSha || $[5] !== pendingScrollTarget || $[6] !== setComparison || $[7] !== setPendingScrollTarget || $[8] !== versionOptions || $[9] !== versionsError || $[10] !== versionsFetching || $[11] !== versionsLoaded || $[12] !== versionsLoading) {
    t1 = () => {
      if (pendingScrollTarget?.type !== "comment" || pendingScrollTarget.comparisonResolved || !pendingScrollTarget.targetHeadSha) {
        return;
      }
      if (!versionsLoaded && !versionsError && (versionsLoading || versionsFetching)) {
        return;
      }
      const targetComparison = resolvePrV2CommentComparison({
        canonicalBaseSha,
        canonicalHeadSha,
        currentBaseSha,
        currentHeadSha,
        targetHeadSha: pendingScrollTarget.targetHeadSha,
        versionOptions
      });
      const requiredComparisonKey = targetComparison ? buildComparisonKey(targetComparison.baseSha, targetComparison.headSha) : comparisonKey;
      if (targetComparison) {
        setComparison(targetComparison);
      }
      setPendingScrollTarget(currentTarget => {
        if (currentTarget?.type !== "comment" || currentTarget.commentId !== pendingScrollTarget.commentId || currentTarget.targetHeadSha !== pendingScrollTarget.targetHeadSha) {
          return currentTarget;
        }
        return {
          ...currentTarget,
          comparisonResolved: true,
          requiredComparisonKey
        };
      });
    };
    t2 = [canonicalBaseSha, canonicalHeadSha, comparisonKey, currentBaseSha, currentHeadSha, pendingScrollTarget, setComparison, setPendingScrollTarget, versionOptions, versionsError, versionsFetching, versionsLoaded, versionsLoading];
    $[0] = canonicalBaseSha;
    $[1] = canonicalHeadSha;
    $[2] = comparisonKey;
    $[3] = currentBaseSha;
    $[4] = currentHeadSha;
    $[5] = pendingScrollTarget;
    $[6] = setComparison;
    $[7] = setPendingScrollTarget;
    $[8] = versionOptions;
    $[9] = versionsError;
    $[10] = versionsFetching;
    $[11] = versionsLoaded;
    $[12] = versionsLoading;
    $[13] = t1;
    $[14] = t2;
  } else {
    t1 = $[13];
    t2 = $[14];
  }
  useEffect(t1, t2);
}